"use client";

import React from "react"

import Image from "next/image";
import {Locale} from "@/i18n-config";
import {useSearchParams} from "next/navigation";
import type {PointOfContact as PointOfContactProps} from "@docid/types/src/payload-types";
import {RichText} from "@/components/content/common/RichText";
import {MdEmail, MdLocalPhone} from "react-icons/md";
import {PointOfContactForm} from "@/components/common/forms/PointOfContactForm";
import {getMediaBySize, isMedia} from "@/utils/content/media";
import clsx from "clsx";

type Props = {
    lang: Locale;
    dictionary?: any;
    className?: string;
    block: PointOfContactProps;
    position?: number;
};

export function PointOfContact({ lang, dictionary, className, block, position } : Props) {

    const searchParams = useSearchParams();

    const success = searchParams.get("success") === "1";
    const error = searchParams.get("error") === "1";

    return (
        <div id="contactForm" className={clsx("bg-white/20 rounded-lg p-10", className)}>
            {success ? (
                <div className="bg-white rounded-md p-6">
                    <h3 className="text-2xl md:text-3xl font-semibold">{block.formSuccessTitle}</h3>
                    <div className="text-lg">
                        <RichText data={block.formSuccessDescription} />
                    </div>
                </div>
            ) : (
                <div className="flex flex-col md:flex-row gap-10">
                    {error && (
                        <div className="bg-rose-100 rounded-md p-6">
                            <h3 className="text-2xl md:text-3xl font-semibold text-rose-700">{block.formErrorTitle}</h3>
                            <div className="text-lg text-rose-700">
                                <RichText data={block.formErrorDescription} />
                            </div>
                        </div>
                    )}
                    <div className="flex flex-grow-0 flex-col gap-4 items-center justify-start md:w-[300px]">
                        {block.pocImage && isMedia(block.pocImage) && (
                            <div>
                                <div className="rounded-xl overflow-hidden w-full max-w-[280px] max-h-[280px] md:w-[280px] md:h-[280px]">
                                    {(() => {
                                        const image = getMediaBySize(block.pocImage, "small");
                                        return (
                                            <Image
                                                style={{ objectFit: "fill" }}
                                                width={image.width}
                                                height={image.height}
                                                src={image.url}
                                                alt={image.alt}
                                                priority={!!(position && position <= 2)}
                                            />
                                        );
                                    })()}
                                </div>
                            </div>
                        )}
                        <div className="text-center">
                            {block.pocName && (
                                <h4 className="font-semibold text-2xl md:text-3xl text-white leading-normal">{block.pocName}</h4>
                            )}
                            {block.pocDescription && (
                                <p className="text-xl leading-normal text-black font-semibold">{block.pocDescription}</p>
                            )}
                        </div>
                        <div>
                            {block.pocEmail && (
                                <p className="text-xl">
                                    <a className="flex flex-row items-center justify-center gap-2 text-white hover:text-black"
                                       href={`mailto:${block.pocEmail}`}>
                                        <MdEmail/>{block.pocEmail}
                                    </a>
                                </p>
                            )}
                            {block.pocPhone && (
                                <p className="text-xl">
                                    <a className="flex flex-row items-center justify-center gap-2 text-white hover:text-black"
                                       href={`tel:${block.pocPhone}`}>
                                        <MdLocalPhone/>{block.pocPhone}
                                    </a>
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex-grow">
                        {block.title && (
                            <h3 className="font-semibold text-2xl md:text-3xl text-white leading-normal text-center md:text-left">{block.title}</h3>
                        )}
                        {block.description && (
                            <p className="mt-5 font-source text-xl text-black leading-normal text-center md:text-left">{block.description}</p>
                        )}
                        <div className="mt-8">
                            <PointOfContactForm
                                lang={lang}
                                dictionary={dictionary} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}